import { useEffect, useMemo, useRef, useState } from 'react';
import { FACTIONS, type MatchSetup } from '../../content/factions';
import type { FactionId, MapId, MatchMode, PlayerId, TeamId } from '../../content/types';
import { mapsForMode } from '../../content/map';
import { OnlineClient, type ConnectionStatus } from '../../net/client';
import type { ServerMessage } from '../../net/protocol';
import { MapPicker } from '../MapPicker';
import menuBg from '../assets/menu-bg.png';
import { Select } from '../components/Select';

export interface OnlineMatchInfo {
  client: OnlineClient;
  matchId: string;
  localPlayer: PlayerId;
  setup: MatchSetup;
  seed: number;
}

type RoomState = Extract<ServerMessage, { type: 'room' }>;

function serverUrl(): string {
  const configured = import.meta.env.VITE_SERVER_URL as string | undefined;
  if (configured) return configured;
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${window.location.hostname}:8787`;
}

export function OnlineLobby({ onStart, onBack }: { onStart: (info: OnlineMatchInfo) => void; onBack: () => void }) {
  const clientRef = useRef<OnlineClient | null>(null);
  const startedRef = useRef(false);
  const onStartRef = useRef(onStart);
  const modeRef = useRef<MatchMode>('1v1');
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [message, setMessage] = useState('Connecting to the game server…');
  const [mode, setMode] = useState<MatchMode>('1v1');
  const [code, setCode] = useState('');
  const [room, setRoom] = useState<RoomState | null>(null);
  const [queued, setQueued] = useState(false);
  const [faction, setFaction] = useState<FactionId>('iron-covenant');
  const [mapVote, setMapVote] = useState<MapId>('crownline-crossing');
  const factions = useMemo(() => Object.values(FACTIONS).filter((entry) => entry.implemented), []);
  const localMember = room?.members.find((member) => member.slot === room.localSlot);
  const ready = Boolean(localMember?.ready);
  onStartRef.current = onStart;
  modeRef.current = mode;

  useEffect(() => {
    const client = new OnlineClient(serverUrl());
    clientRef.current = client;
    const offStatus = client.onStatus((next) => {
      setStatus(next);
      if (next === 'connected') setMessage('Connected. Create a room, join one, or enter the public queue.');
      if (next === 'reconnecting') setMessage('Connection interrupted — reconnecting…');
    });
    const offMessage = client.onMessage((incoming) => {
      if (incoming.type === 'room') {
        setRoom(incoming);
        setMode(incoming.mode);
        const mine = incoming.members.find((member) => member.slot === incoming.localSlot);
        if (mine?.mapVote) setMapVote(mine.mapVote);
        setQueued(false);
        setMessage(incoming.members.length === incoming.capacity ? 'Choose your team, faction, map vote, and ready up.' : 'Share the room code with the other players.');
      } else if (incoming.type === 'queued') {
        setQueued(true);
        setMessage(`Waiting in the ${modeRef.current} public queue — position ${incoming.position}.`);
      } else if (incoming.type === 'ack' && !incoming.ok) {
        setMessage(incoming.reason ?? 'The server rejected that request.');
      } else if (incoming.type === 'matchFound' || incoming.type === 'resumeMatch') {
        startedRef.current = true;
        onStartRef.current({ client, matchId: incoming.matchId, localPlayer: incoming.player, setup: incoming.setup, seed: incoming.seed });
      } else if (incoming.type === 'error') setMessage(incoming.message);
    });
    client.connect();
    return () => {
      offStatus();
      offMessage();
      if (!startedRef.current) client.close();
    };
  }, []);

  const changeMode = (next: MatchMode) => {
    if (room || queued) return;
    setMode(next);
    setMapVote(mapsForMode(next)[0].id);
  };
  const deck = [...FACTIONS[faction].defaultDeck];
  const vote = (next: MapId) => {
    if (ready) return;
    setMapVote(next);
    clientRef.current?.setRoomMapVote(next);
  };
  const setTeam = (team: TeamId) => {
    if (!ready) clientRef.current?.setRoomTeam(team);
  };
  const toggleReady = () => clientRef.current?.ready(!ready, faction, deck);

  return (
    <div className="menu onlinemenu screen-in" style={{ background: `#0a0c12 url(${menuBg}) center / cover no-repeat` }}>
      <div className="onlineshade" />
      <div className="ornframe onlinewide">
        <div className="parchment onlinepanel">
          <div className="onlinetitlerow"><h1 className="onlinetitle">Play Online</h1></div>
          <div className="onlinesub">{status} · authoritative multiplayer</div>

          {!room && !queued && (
            <>
              <div className="modetabs dark" role="tablist" aria-label="Online format">
                <button className={mode === '1v1' ? 'selected' : ''} onClick={() => changeMode('1v1')}>1v1</button>
                <button className={mode === '2v2' ? 'selected' : ''} onClick={() => changeMode('2v2')}>2v2</button>
              </div>
              <div className="factionrow">
                <span className="factionlabel">Faction</span>
                <div className="factionplaque"><span className="plaquemark">{FACTIONS[faction].mark}</span><Select ariaLabel="Faction" value={faction} options={factions.map((entry) => ({ value: entry.id, label: entry.name }))} onChange={(id) => setFaction(id as FactionId)} /></div>
              </div>
              <div className="bigbtnrow">
                <button className="ornbtn goldbtn" disabled={status !== 'connected'} onClick={() => clientRef.current?.createRoom(mode)}>Create {mode} friend room</button>
                <button className="ornbtn darkbtn" disabled={status !== 'connected'} onClick={() => clientRef.current?.queue(mode, faction, deck)}>Play public {mode}</button>
              </div>
              <div className="codebtnrow">
                <input className="codeinput" value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} maxLength={8} placeholder="ROOM CODE" />
                <button className="ornbtn bronzebtn" disabled={status !== 'connected' || code.length !== 8} onClick={() => clientRef.current?.joinRoom(code)}>Join room</button>
              </div>
            </>
          )}

          {queued && <div className="roomstate"><div className="roominfo">{message}<br />The server will choose a random {mode} battlefield.</div></div>}

          {room && (
            <div className="roomlobby">
              <div className="roomcode">{room.code}</div>
              <div className="roominfo">{room.members.length}/{room.capacity} players · {room.mode}</div>
              <div className="roommembers">
                {Array.from({ length: room.capacity }, (_, index) => {
                  const member = room.members.find((entry) => entry.slot === index);
                  return <div key={index} className={`roommember ${member ? `team-${member.team}` : 'empty'}`}><b>{member?.slot === room.localSlot ? 'You' : `Player ${index + 1}`}</b><span>{member ? `${member.faction ? FACTIONS[member.faction].mark : '—'} · Team ${member.team === 0 ? 'Blue' : 'Gold'} · ${member.ready ? 'Ready' : 'Not ready'}` : 'Waiting…'}</span></div>;
                })}
              </div>
              {room.mode === '2v2' && (
                <div className="teamchoices">
                  <button disabled={ready} className={localMember?.team === 0 ? 'selected' : ''} onClick={() => setTeam(0)}>Team Blue</button>
                  <button disabled={ready} className={localMember?.team === 1 ? 'selected' : ''} onClick={() => setTeam(1)}>Team Gold</button>
                </div>
              )}
              <label className="roomfaction">Your faction<Select ariaLabel="Your faction" disabled={ready} value={faction} options={factions.map((entry) => ({ value: entry.id, label: entry.name }))} onChange={(id) => setFaction(id as FactionId)} /></label>
              <MapPicker mode={room.mode} selected={mapVote} onSelect={vote} disabled={ready} votes={room.mapVotes} />
              <button className={`ornbtn ${ready ? 'bronzebtn' : 'goldbtn'} compact`} onClick={toggleReady}>{ready ? 'Cancel Ready' : 'Ready to battle'}</button>
            </div>
          )}
          <div className="statusline"><span className={`statusdot ${status}`} />{message}</div>
        </div>
      </div>
      <button className="linkbtn ornback" onClick={onBack}>Back to Play</button>
    </div>
  );
}
